import React, { useEffect, useState, Suspense, lazy, Component } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { defaultSEO, seoForPath, isKnownRoute } from './config/seo';
import { Layout, Spin } from 'antd';
import styled, { ThemeProvider } from 'styled-components';
import { Element } from 'react-scroll';
import { SERVICES } from './config/servicesConfig';
import './App.css';

import { theme } from './theme/theme';
import { ThemeProvider as LiquidGlassThemeProvider } from './contexts/ThemeContext';
import { LiquidGlassCard } from './components/LiquidGlass/index';

// PERF: Critical above-the-fold components loaded synchronously
import Hero from './components/Hero';
import About from './components/About';
import HeaderComponent from './components/Header';
import DynamicSkyBackground from './components/DynamicSkyBackground';
import Footer from './components/Footer';
// DWEB-121: DynaMite is the AI Phone Receptionist in text — the ElevenLabs
// chat surface, the site's own DOM driving the ElevenLabs SDK (v6.5.0),
// pointed at the web agent. The on-device Chatbot.js (and the eager import
// that let MediaPipe initialise the GPU during page load, c2d6fe6) is gone
// with the model it hosted.
import DynaMiteWidget from './components/DynaMiteWidget';
// DWEB-15: Google Analytics consent. The DynaMite chat carries its own notice
// before the first message — the two are separate.
import ConsentBanner from './components/ConsentBanner';
import { scrollToSection } from './utils/scrollToSection';

import MountNearViewport from './components/MountNearViewport';

// PERF: Below-the-fold and route-specific components lazy loaded
const AppDemos = lazy(() => import('./components/AppDemos'));
// DWEB-193: AppDemos brings three.js, so it mounts only NEAR the viewport
// (MountNearViewport); until then its placeholder reserves the section's
// height, measured on the LOCAL build at 2945ef8 (demo not engaged, images
// loaded): 4,562 @375 · 4,064 @412 · 2,541 @768 · 2,389 @1280 and @1440.
// DWEB-26: every reserve below is the section's OUTER height — its settled
// height plus its own margin-bottom (32 px at <=991 wide, 48 px at >=992;
// margin-top is 0 on every lazy section), because a placeholder carries no
// margin. Measured 2026-09-12: reserves of the bare height left each placeholder
// one margin short, so a section mounting above the viewport shifted the page
// by 48 px per section and MountNearViewport's correction (which compares
// heights) missed exactly that — six sections added 315 px under a page that
// had just been scrolled to its bottom, and the footer hid. The apps section's
// original reserve carried the same miss.
const APP_DEMOS_RESERVE = { phone: 4332, tablet: 2582, desktop: 2438 };
const Services = lazy(() => import('./components/Services'));
const Partners = lazy(() => import('./components/Partners'));
// DWEB-48: the "Shipped under constraints" proof section. Lazy like its
// neighbours — it is below the fold and its only cost is text.
const Proof = lazy(() => import('./components/Proof'));
const FAQ = lazy(() => import('./components/FAQ'));
const Contact = lazy(() => import('./components/Contact'));
// DWEB-26: the other five lazy sections mount near the viewport too, so a load
// that never scrolls fetches none of their chunks (Contact's alone carries
// axios). Heights measured 2026-09-12 on the LOCAL build at 5571e17, every
// section settled with images loaded:
//   services  5,318 @375 · 5,230 @390 · 4,853 @412 · 3,397 @768 · 2,542 @1280/1440
//   proof     6,184 @375 · 5,999 @390 · 5,586 @412 · 3,402 @768 · 3,902 @1280/1440
//   contact   1,121 @375/390/412 · 863 @768 · 943 @1280/1440
//   faq       1,669 @375 · 1,642 @390 · 1,504 @412 · 977 @768 · 1,079 @1280/1440
//   partners  1,015 @375/390/412 · 494 @768 · 555 @1280/1440
// Services sits inside the 2,000 px margin on tablet and desktop, so it still
// mounts at load there; on phones it starts ~3,500 px down and defers.
// Outer heights (the settled heights above + margin-bottom 32 / 32 / 48):
const SERVICES_RESERVE = { phone: 5132, tablet: 3432, desktop: 2588 };
const PROOF_RESERVE = { phone: 5932, tablet: 3432, desktop: 3948 };
const CONTACT_RESERVE = { phone: 1152, tablet: 892, desktop: 988 };
const FAQ_RESERVE = { phone: 1632, tablet: 1012, desktop: 1128 };
const PARTNERS_RESERVE = { phone: 1047, tablet: 527, desktop: 603 };
const Blog = lazy(() => import('./components/Blog'));
// DWEB-188: the first article published on this site. Its own lazy chunk, so
// neither `/` nor the blog index pays for ~2,700 words and two diagrams.
const BlogPost = lazy(() => import('./components/BlogPost'));
const ContentRenderer = lazy(() => import('./components/ContentRenderer'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
// DWEB-190: the applications page. A lazy chunk `/` never fetches.
const Work = lazy(() => import('./components/Work'));
const ServicePage = lazy(() => import('./components/ServicePage'));
const NotFound = lazy(() => import('./components/NotFound'));


// Per-route Error Boundary (standards brief: every top-level route must be wrapped)
class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[RouteErrorBoundary] Caught error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
          <h2>Something went wrong loading this page.</h2>
          <p>{this.state.error?.message}</p>
          <button onClick={() => this.setState({ hasError: false, error: null })}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading spinner for Suspense fallback
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
`;

const { Content } = Layout;

const StyledLayout = styled(Layout)`
  background: transparent !important;
  
  .ant-layout {
    background: transparent !important;
  }
`;

/**
 * Head tags that exist BOTH statically in public/index.html (for scrapers that
 * do not run JavaScript) and via Helmet (per-route, for everyone else).
 *
 * Helmet appends rather than replaces, so without this the rendered DOM ends up
 * with two canonicals and two descriptions — and two canonicals is worse than
 * none, because the crawler picks one arbitrarily. Helmet marks everything it
 * owns with `data-rh`, so the static twin is the one without it.
 */
const HELMET_MANAGED_STATIC_TAGS = [
  'link[rel="canonical"]',
  'meta[name="description"]',
  'meta[property="og:type"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[property="og:site_name"]',
  'meta[name="twitter:card"]',
  'meta[name="twitter:site"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
];

const SEOWrapper = () => {
  const location = useLocation();
  const seoData = seoForPath(location.pathname);

  // DWEB-74. A 404 must not nominate itself as canonical.
  //
  // The catch-all route already sends `noindex, follow`, and pairing that with
  // a self-referencing canonical is contradictory signalling: one tag says "do
  // not index this URL", the next says "this URL is the preferred version of
  // itself". On a site with a live soft-404 report that ambiguity is not free,
  // so on an unknown path the canonical and og:url are omitted outright — no
  // canonical at all is the honest statement about a page that is not a page.
  // Known routes are unchanged.
  const known = isKnownRoute(location.pathname);

  // Drop the static twins once React is running. The served HTML keeps them —
  // that is the whole point of DWEB-32 — this only de-duplicates the live DOM.
  useEffect(() => {
    HELMET_MANAGED_STATIC_TAGS.forEach((selector) => {
      document
        .head
        .querySelectorAll(`${selector}:not([data-rh])`)
        .forEach((el) => el.remove());
    });
  }, []);

  return (
    <Helmet>
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      {known && <link rel="canonical" href={`${defaultSEO.canonical}${location.pathname}`} />}

      {/* OpenGraph Tags */}
      <meta property="og:type" content={defaultSEO.openGraph.type} />
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      {known && <meta property="og:url" content={`${defaultSEO.openGraph.url}${location.pathname}`} />}
      <meta property="og:site_name" content={defaultSEO.openGraph.site_name} />

      {/* Twitter Tags */}
      <meta name="twitter:card" content={defaultSEO.twitter.cardType} />
      <meta name="twitter:site" content={defaultSEO.twitter.site} />
      <meta name="twitter:creator" content={defaultSEO.twitter.handle} />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
    </Helmet>
  );
};

const StyledContent = styled(Content)`
  padding-top: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: transparent !important;
`;

const MainContent = styled(LiquidGlassCard)`
  flex: 1;
  margin: 0;
  padding: 0;
  border-radius: 0;
  min-height: 100vh;
  
  /* Remove static background - now using dynamic sky */
  background: transparent;
  
  /* Make LiquidGlass completely transparent */
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
`;




function App() {
  // DWEB-193: a load that arrives WITH a section target (/#contact, or the
  // header's scrollTarget from another route) mounts the apps section at once,
  // so the jump below is computed against the real section, not its
  // placeholder. Read in an initializer, before the effect clears scrollTarget.
  const [hasInitialTarget] = useState(() =>
    window.location.pathname === '/' &&
    Boolean(sessionStorage.getItem('scrollTarget') || window.location.hash.replace('#', '')));

  // Handle scroll target from sessionStorage when homepage loads
  useEffect(() => {
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    const hashTarget = window.location.hash.replace('#', '');
    const targetSection = scrollTarget || hashTarget;

    if (targetSection && window.location.pathname === '/') {
      // Clear the stored target
      if (scrollTarget) {
        sessionStorage.removeItem('scrollTarget');
      }

      // Wait for components to render, then scroll
      const timer = setTimeout(() => {
        scrollToSection(targetSection);
      }, 500); // Increased delay to ensure all components are rendered

      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <LiquidGlassThemeProvider>
          <ThemeProvider theme={theme}>
            <SEOWrapper />
            <DynamicSkyBackground />
            <StyledLayout className="layout">
              <HeaderComponent />
              <StyledContent>
                <MainContent variant="auto">
                  {/* PERF: Suspense wrapper for lazy-loaded route components */}
                  <Routes>
                    <Route path="/" element={
                      <RouteErrorBoundary>
                        {/* DWEB-196: Hero and About are EAGER imports, so they sit
                            OUTSIDE the boundary. Inside it they waited on the
                            slowest lazy section below them, and the hero h1 is
                            the LCP element. The lazy sections keep one boundary
                            between them: one spinner, one layout change. */}
                        <Element name="hero"><Hero id="hero" /></Element>
                        <Element name="about"><About id="about" /></Element>
                        <Suspense fallback={<LoadingSpinner><Spin size="large" /></LoadingSpinner>}>
                          <>
                            {/* DWEB-124 (v6.5.2): Services ABOVE the apps. The
                                review's finding: "a visitor arriving for a
                                fractional CTO scrolls past six consumer apps
                                first." The five offers are the business; the
                                apps are proof and colour. Proof stays directly
                                after Services so the TaxMD engagement is read
                                beside the Fractional CTO offer it demonstrates
                                (the ticket's "strongest-fit proof next to the
                                offer it proves"); the apps follow as supporting
                                evidence, still above Contact. Chosen on
                                JUDGEMENT, not measurement — the site has no
                                analytics, so scroll depth is unobservable, and
                                the ticket asks for that to be said plainly.
                                Sections are addressed by Element NAME (the
                                header's scrollTarget values and the hero CTAs),
                                so a reorder here cannot break in-page
                                navigation; e2e/homepage-order.spec.js pins
                                this order, and site-sweep.spec.js walks every
                                nav item to its section. No nav item for Proof —
                                the header's seven items are the limit. */}
                            {/* DWEB-26: every lazy section mounts near the viewport,
                                each in its own Suspense (MountNearViewport), with
                                its height reserved until then. */}
                            <Element name="services">
                              <MountNearViewport id="services" reserve={SERVICES_RESERVE} eager={hasInitialTarget}>
                                <Services id="services" />
                              </MountNearViewport>
                            </Element>
                            <Element name="proof">
                              <MountNearViewport id="proof" reserve={PROOF_RESERVE} eager={hasInitialTarget}>
                                <Proof id="proof" />
                              </MountNearViewport>
                            </Element>
                            {/* DWEB-193: mounted near the viewport, in its own
                                Suspense — it used to suspend this route's one
                                boundary, so the hero h1 waited for three.js. */}
                            <Element name="app-demos">
                              <MountNearViewport id="app-demos" reserve={APP_DEMOS_RESERVE} eager={hasInitialTarget}>
                                <AppDemos id="app-demos" />
                              </MountNearViewport>
                            </Element>
                            <Element name="contact">
                              <MountNearViewport id="contact" reserve={CONTACT_RESERVE} eager={hasInitialTarget}>
                                <Contact id="contact" />
                              </MountNearViewport>
                            </Element>
                            <Element name="faq">
                              <MountNearViewport id="faq" reserve={FAQ_RESERVE} eager={hasInitialTarget}>
                                <FAQ id="faq" />
                              </MountNearViewport>
                            </Element>
                            <Element name="partners">
                              <MountNearViewport id="partners" reserve={PARTNERS_RESERVE} eager={hasInitialTarget}>
                                <Partners id="partners" />
                              </MountNearViewport>
                            </Element>
                          </>
                        </Suspense>
                      </RouteErrorBoundary>
                    } />
                    <Route path="/blog" element={
                      <RouteErrorBoundary>
                        <Suspense fallback={<LoadingSpinner><Spin size="large" /></LoadingSpinner>}>
                          <Blog id="blog" />
                        </Suspense>
                      </RouteErrorBoundary>
                    } />
                    {/* DWEB-188. The slug is written out rather than matched as
                        /blog/:slug: seo.js enumerates it for the same reason it
                        enumerates the five service slugs, and a param route here
                        would make /blog/anything render an article shell that
                        isKnownRoute() calls a 404. */}
                    <Route path="/blog/how-we-run-fractional-cto-engagements" element={
                      <RouteErrorBoundary>
                        <Suspense fallback={<LoadingSpinner><Spin size="large" /></LoadingSpinner>}>
                          <BlogPost />
                        </Suspense>
                      </RouteErrorBoundary>
                    } />
                    <Route path="/content/:fileName" element={
                      <RouteErrorBoundary>
                        <Suspense fallback={<LoadingSpinner><Spin size="large" /></LoadingSpinner>}>
                          <ContentRenderer />
                        </Suspense>
                      </RouteErrorBoundary>
                    } />
                    <Route path="/privacy-policy" element={
                      <RouteErrorBoundary>
                        <Suspense fallback={<LoadingSpinner><Spin size="large" /></LoadingSpinner>}>
                          <PrivacyPolicy />
                        </Suspense>
                      </RouteErrorBoundary>
                    } />
                    <Route path="/work" element={
                      <RouteErrorBoundary>
                        <Suspense fallback={<LoadingSpinner><Spin size="large" /></LoadingSpinner>}>
                          <Work />
                        </Suspense>
                      </RouteErrorBoundary>
                    } />
                    {/* DWEB-105 — one route per offer, MAPPED from SERVICES
                        rather than written out five times. servicesConfig.js
                        derives each `path` from the offer's id, so the router,
                        the prerender and the sitemap cannot disagree about
                        which five pages exist. seo.js carries the same five as
                        pageSEO keys because it must stay import-free;
                        service-pages.spec.js holds those two lists equal, and
                        routes.spec.js holds each path to isKnownRoute. */}
                    {SERVICES.map((service) => (
                      <Route key={service.id} path={service.path} element={
                        <RouteErrorBoundary>
                          <Suspense fallback={<LoadingSpinner><Spin size="large" /></LoadingSpinner>}>
                            <ServicePage offerId={service.id} />
                          </Suspense>
                        </RouteErrorBoundary>
                      } />
                    ))}
                    {/* Catch-all. Amplify still answers 200 (infrastructure is
                        out of scope for v6.0); the noindex in NotFound is what
                        stops these URLs being indexed — review finding C3. */}
                    <Route path="*" element={
                      <RouteErrorBoundary>
                        <Suspense fallback={<LoadingSpinner><Spin size="large" /></LoadingSpinner>}>
                          <NotFound />
                        </Suspense>
                      </RouteErrorBoundary>
                    } />
                  </Routes>
                </MainContent>
              </StyledContent>
            </StyledLayout>
            <Footer />
            <DynaMiteWidget />
            <ConsentBanner />
          </ThemeProvider>
        </LiquidGlassThemeProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
